import { useEffect, useId, useRef, useState } from "react";
import { ClientApiError } from "../auth/api";
import { listPublicDogs, type DogSummary } from "./api";

export function DogAutocomplete({
  label,
  placeholder,
  expectedSex,
  value,
  onSelect,
}: {
  label: string;
  placeholder?: string;
  expectedSex?: "male" | "female";
  value: DogSummary | null;
  onSelect: (dog: DogSummary | null) => void;
}) {
  const inputId = useId();
  const listId = useId();
  const blurTimer = useRef<number | null>(null);
  const [query, setQuery] = useState(value?.registeredName ?? "");
  const [items, setItems] = useState<DogSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => setQuery(value?.registeredName ?? ""), [value]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listPublicDogs({ q: query.trim(), limit: 12 });
        if (controller.signal.aborted) return;
        const filtered = expectedSex
          ? page.items.filter((dog) => dog.sex === expectedSex)
          : page.items;
        setItems(filtered);
        setActive(filtered.length > 0 ? 0 : -1);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setItems([]);
        if (caught instanceof ClientApiError) {
          setError({ message: caught.problem.message, requestId: caught.problem.requestId });
        } else {
          setError({ message: "Không thể tìm hồ sơ chó." });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [expectedSex, open, query]);

  function choose(dog: DogSummary) {
    setQuery(dog.registeredName);
    setOpen(false);
    onSelect(dog);
  }

  return (
    <div className="dog-autocomplete">
      <label className="ui-field" htmlFor={inputId}>
        <span className="ui-field__label">{label}</span>
        <div className="dog-autocomplete__control">
          <input
            id={inputId}
            className="ui-control"
            type="search"
            role="combobox"
            autoComplete="off"
            value={query}
            placeholder={placeholder}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 && items[active] ? `${listId}-${items[active].id}` : undefined}
            onFocus={() => {
              if (blurTimer.current) window.clearTimeout(blurTimer.current);
              setOpen(true);
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setOpen(false), 150);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              if (value && event.target.value !== value.registeredName) onSelect(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setOpen(true);
                setActive((current) => Math.min(items.length - 1, current + 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((current) => Math.max(0, current - 1));
              } else if (event.key === "Enter" && active >= 0 && items[active]) {
                event.preventDefault();
                choose(items[active]);
              } else if (event.key === "Escape") {
                event.preventDefault();
                setOpen(false);
              }
            }}
          />
          {value ? (
            <button
              type="button"
              className="dog-autocomplete__clear"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSelect(null)}
            >
              Xóa
            </button>
          ) : null}
        </div>
      </label>
      {open ? (
        <div className="dog-autocomplete__panel" id={listId} role="listbox" aria-label={`Kết quả ${label}`}>
          {loading ? <p role="status">Đang tìm hồ sơ…</p> : null}
          {!loading && error ? (
            <div role="alert">
              <strong>Không thể tải kết quả</strong>
              <span>{error.message}</span>
              {error.requestId ? <code>Mã yêu cầu: {error.requestId}</code> : null}
            </div>
          ) : null}
          {!loading && !error && items.length === 0 ? <p>Không có hồ sơ phù hợp.</p> : null}
          {!loading && !error ? items.map((dog, index) => (
            <button
              key={dog.id}
              id={`${listId}-${dog.id}`}
              type="button"
              role="option"
              aria-selected={active === index}
              className="dog-autocomplete__option"
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(dog)}
            >
              <span>
                <strong>{dog.registeredName}</strong>
                <small>{dog.breed?.nameVi ?? "Chưa rõ giống"} · {dog.dateOfBirth ?? "Chưa rõ ngày sinh"}</small>
              </span>
              <code>{dog.sex === "male" ? "Đực" : dog.sex === "female" ? "Cái" : "Chưa rõ"}</code>
            </button>
          )) : null}
        </div>
      ) : null}
    </div>
  );
}
